import fp from 'lodash/fp';
import React from 'react';
import { Badge, Button, Row } from 'react-bootstrap';
import { connect } from "react-redux";
import { LinkContainer } from 'react-router-bootstrap';
import Spinner from 'react-spinkit';
import { compose, withHandlers, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes } from '../../../../constants';
import { fullName } from '../../../../services';
import { businessActions, businessSelector, teamMembersSelector } from '../../../../state/modules/business';
import { pluralise } from '../../../../utils';
import { Icon, SortableTable, UserAvatar } from '../../../components';

const columns = [
  {
    name: 'name',
    display: () => 'Name',
    className: () => "name",
  },
]

export const Review = compose(
  connect(
    (state) => ({
      allTeamMembers: state.business.teamMembers
    }),
    (dispatch) => ({
    })
  ),
  withProps(
    ({ allTeamMembers, team }) => ({
      rows: fp.flow(
        fp.filter(
          ({ user }) => fp.contains(user.id)(team.members.value)
        ),
        fp.map(
          ({ user, isTeamManager }) => ({
            name: <div className='left'>
              <UserAvatar userName={fullName(user)} avatar={user.avatar} />
              <span className='member-name'>
                {fullName(user)}
              </span>
              {
                fp.contains(user.id)(team.managers.value) ?
                  <Badge bsClass='team-manager-badge'>Team manager</Badge> : null
              }
            </div>
          })
        )
      )(allTeamMembers),
      teamManagers: fp.flow(
        fp.filter(
          ({ user }) => fp.contains(user.id)(team.nonMemberManagers.value)
        ),
        fp.map(fp.get('user'))
      )(allTeamMembers).concat(fp.flow(
        fp.filter(
          ({ user }) => fp.contains(user.id)(team.managers.value)
        ),
        fp.map(fp.get('user'))
      )(allTeamMembers))
    })
  )
)(
  ({ team, rows, stepBack, teamManagers }) => (
    <div id='review'>
      <Row className='no-gutter'>
        <form className='boxed'>
          <div className='flexbox justify-between'>
            <div>
              <h2 className='no-margin flex-center'>{team.name.value}</h2>
            </div>
            <div>
              <Button className='flex-center no-padding' bsStyle='plain' onClick={stepBack}><Icon icon='edit-medium' />Edit team name</Button>
            </div>
          </div>
        </form>
      </Row>

      <Row id='team-members-heading' className='no-gutter'>
        <div className='flexbox justify-between align-baseline'>
          <div>
            <h2>
              Team managers
            </h2>
          </div>
        </div>
      </Row>

      <Row className='no-gutter'>
        {
          teamManagers.map((user, index) => (
            <Badge key={index} bsClass='team-manager-badge'>{user.firstName} {user.lastName}</Badge>
          ))
        }
      </Row>

      <Row id='team-members-heading' className='no-gutter'>
        <div className='flexbox justify-between align-baseline'>
          <div>
            <h2>
              Team members
            <Badge bsClass='highlight-badge left-gap'>{`${team.members.value.length} ${fp.upperCase(pluralise('member', team.members.value.length))}`}</Badge>
            </h2>
          </div>
          <div>
            <Button className='flex-center' bsStyle='plain' onClick={stepBack}><Icon icon='edit-medium' />Edit team members</Button>
          </div>
        </div>
      </Row>

      <Row className='no-gutter'>
        <form>
          <SortableTable className='members-table' columns={columns} rows={rows} emptyData='You have no team member selected' />
        </form>
      </Row>
    </div>
  )
);

export const ReviewFooter = compose(
  connect(
    (state) => ({
      teamMembers: teamMembersSelector(state),
      business: businessSelector(state),
      isUpdatingTeam: state.business.isSettingTeams || state.business.isFetchingTeams
    }),
    (dispatch) => ({
      createTeam: bindActionCreators(businessActions.business.team.createTeam, dispatch),
      updateTeam: bindActionCreators(businessActions.business.team.update, dispatch)
    })
  ),
  withProps(
    ({ team, teamMembers, teamEditing }) => {
      const newTeamName = team.name.value
      const currentTeamMembers = fp.filter(
        ({ user }) => fp.contains(user.id)(team.members.value)
      )(teamMembers)
      const users = fp.flow(
        fp.map(fp.get('user'))
      )(currentTeamMembers)
      const managers = fp.flow(
        fp.filter(
          ({ id }) => fp.contains(id)(team.managers.value)
        )
      )(users)
      const membersFromOtherTeams = fp.filter(
        ({ teamIDs }) => {
          const currentTeamId = teamEditing ? [fp.get('businessTeam.id')(teamEditing)] : []
          const otherTeams = fp.difference(teamIDs, currentTeamId)
          return otherTeams.length > 0
        }
      )(currentTeamMembers)

      const nonMemberManagers = fp.flow(
        fp.filter(
          ({ user }) => fp.contains(user.id)(team.nonMemberManagers.value)
        ),
        fp.map(fp.get('user'))
      )(teamMembers)

      return {
        newTeamName,
        currentTeamMembers,
        users,
        managers,
        nonMemberManagers,
        membersFromOtherTeams
      }
    }
  ),
  withHandlers({
    updateTeam: ({ updateTeam, newTeamName, users, managers, nonMemberManagers, teamEditing, business }) => () => {
      const newTeam = {
        ...fp.get('businessTeam')(teamEditing),
        name: newTeamName
      }
      updateTeam(business.id, newTeam.id, newTeam, users, managers, nonMemberManagers)
    },
    createTeam: ({ createTeam, newTeamName, business, users, managers, nonMemberManagers }) => () => {
      createTeam(business.id, newTeamName, users, managers, nonMemberManagers)
    }
  }),
  withHandlers({
    submitTeam: ({ teamEditing, updateTeam, createTeam, membersFromOtherTeams, toggleConfirmation }) => () => {
      if (teamEditing) {
        updateTeam()
      } else {
        createTeam()
      }
    }
  })
)(
  ({ stepBack, team, submitTeam, isUpdatingTeam, teamEditing, showConfirmation, toggleConfirmation, membersFromOtherTeams }) => (
    <div id='review-footer' className='flexbox justify-between align-center'>
      <div>
        <Button bsStyle='plain' onClick={stepBack}><Icon icon='chevron-left-large' />Back</Button>
      </div>
      <div>
        <LinkContainer to={teamEditing ? routes.app.teams.teamMembers(teamEditing.businessTeam.id) : routes.app.teams.root}>
          <Button bsStyle='plain'>Cancel</Button>
        </LinkContainer>
        {
          isUpdatingTeam ?
            <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
            teamEditing ?
              <Button onClick={submitTeam} disabled={team.members.value.length === 0}>Update Team<Icon className='left-gap' icon='chevron-right-large' /></Button> :
              <Button onClick={submitTeam} disabled={team.members.value.length === 0}>Create Team<Icon className='left-gap' icon='chevron-right-large' /></Button>
        }
      </div>
    </div>
  )
);


// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/review.js